/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { useExtraRequestParameters } from '@/data/Content/_ExtraRequestParameters';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { productFetcher } from '@/data/Content/_Product';
import { getContractIdParamFromContext, getSettings, Settings, useSettings } from '@/data/Settings';
import { getUser, useUser } from '@/data/User';
import { getPageDataFromId, usePageDataFromId } from '@/data/_PageDataFromId';
import { getServerCacheScope } from '@/data/cache/getServerCacheScope';
import { ID } from '@/data/types/Basic';
import { ContentProps } from '@/data/types/ContentProps';
import { ProductType, ResponseProductAttribute, ResponseProductType } from '@/data/types/Product';
import { constructRequestParamsWithPreviewToken } from '@/data/utils/constructRequestParams';
import { extractContentsArray } from '@/data/utils/extractContentsArray';
import { getClientSideCommon } from '@/data/utils/getClientSideCommon';
import { getServerSideCommon } from '@/data/utils/getServerSideCommon';
import { expand, shrink } from '@/data/utils/keyUtil';
import { RequestParams } from 'integration/generated/query/http-client';
import { isNil, omitBy } from 'lodash';
import { GetServerSidePropsContext } from 'next';
import useSWR, { unstable_serialize as unstableSerialize } from 'swr';
import { USPDeliveryTerms } from '../types/USPDeliveryTerms';
import { REQUIRED_ATTRIBUTES } from '../constants/uspsDeliveryTerms';
import { getLocalization } from '@/data/Localization';
import { findAttributeByIdentifier, getAttributeValueByIdentifier } from '@/utils/productAttributes';

const DATA_KEY = 'USP_DELIVERY_TERMS';

export const filterByRequiredAttrs = (attr:ResponseProductAttribute)=>{
	return REQUIRED_ATTRIBUTES.includes(attr.identifier);
}

const fetcher =
	(pub: boolean, context?: GetServerSidePropsContext) =>
	/**
	 * The product details data fetcher.
	 * @param props The props used for the request to build query.
	 * @param params The RequestParams, it contains all the info that a request needed except for 'body' | 'method' | 'query' | 'path'.
	 *                                  we are using it to send cookie header.
	 * @returns product details required to display USP Delivery terms
	 */
	async (
		props: {
			storeId: string;
			catalogId: string;
			tokenName: string;
			tokenExternalValue: string;
			tokenValue: string;
			langId: string;
		} & Record<string, ID | ID[] | boolean | undefined>,
		params: RequestParams
	): Promise<any | undefined> => {

		const { storeId, catalogId, tokenExternalValue, langId, contractId } =
			props;
		const res = (await productFetcher(pub, context)({
			storeId,
			catalogId,
			langId,
			partNumber: [tokenExternalValue],
			contractId,
		}, params)) as unknown as ResponseProductType;
		return extractContentsArray(res)?.at(0) ?? null;
	};

const dataMap = (data: ProductType, settings: Settings): USPDeliveryTerms => {
	const buyable = data?.buyable;
	const seoPath = data?.UserData?.find((obj:Record<string,any>)=>Object.keys(obj).includes("seoPath"));
	const displayToUser = Object.keys(seoPath?.seoPath).length >0; 
	const offerPrice = data?.price?.find(o => o.usage === 'Offer')?.value;
	const checkPrice = parseFloat(offerPrice ?? '0');
	const attrs = data?.attributes;
	const msgDeliveryTerm = getAttributeValueByIdentifier(attrs, 'ITNL_DeliveryTerm');
	const itnlPickupInStore = findAttributeByIdentifier(attrs, 'ITNL_PickupInStore');
	const pickUpInStoreAllowed = itnlPickupInStore?.values?.at(0)?.identifier === 'true'
		&& itnlPickupInStore?.usage === 'Descriptive';
	const preSaleCatentry = getAttributeValueByIdentifier(attrs, 'ITNL_DeliveryTerm') ? true : false;
	const {
		orderCheckoutMinimum,
		orderFreeShippingMinimum,
		orderActualCheckoutMinimum,
		orderActualFreeShippingMinimum,
		StaticHostSecure
	} = settings.userData;
	
	const orderShippingMinimumFinal = parseFloat(orderCheckoutMinimum ?? '0');
	const orderFreeShippingMinimumFinal = parseFloat(orderFreeShippingMinimum ?? '0');
	const orderShippingMinimumActualFinal = parseFloat(orderActualCheckoutMinimum ?? '0');
	const orderFreeShippingMinimumActualFinal = parseFloat(orderActualFreeShippingMinimum ?? '0');
	const uspDeliveryTerms = {
		checkPrice,
		msgDeliveryTerm,
		pickUpInStoreAllowed,
		preSaleCatentry,
		hostURL: StaticHostSecure ?? '',
		orderShippingMinimumFinal,
		orderFreeShippingMinimumActualFinal,
		orderFreeShippingMinimumFinal,
		orderShippingMinimumActualFinal,
		buyable,
		displayToUser
	}
	return omitBy(uspDeliveryTerms, isNil) as USPDeliveryTerms;
};

export const getUSPsDeliveryTerms = async ({ cache, id: _id, context }: ContentProps) => {
	const pageData = await getPageDataFromId(cache, context.query.path, context);
	const { tokenName = '', tokenValue = '', tokenExternalValue = '' } = pageData ?? {};
	const settings = await getSettings(cache, context);
	const user = await getUser(cache, context);
	const { storeId, defaultCatalogId: catalogId, langId } = getServerSideCommon(settings, context);
	await getLocalization(cache, context.locale || 'nl-NL', 'USPDeliveryTerms');
	const props = {
		tokenName,
		tokenValue,
		tokenExternalValue,
		storeId,
		catalogId,
		langId,
		...getContractIdParamFromContext(user?.context),
	};
	
	const cacheScope = getServerCacheScope(context, user.context);
	const key = unstableSerialize([shrink(props), DATA_KEY]);
	const params = constructRequestParamsWithPreviewToken({ context });
	
	const value = cache.get(key, cacheScope) || dataMap(await fetcher(false, context)(props, params),settings);
	cache.set(key, value, cacheScope);
	return value;
};

export const useUSPsDeliveryTerms = () => {
	const router = useNextRouter();
	const { settings } = useSettings();
	const { user } = useUser();
	const { storeId, langId, defaultCatalogId: catalogId } = getClientSideCommon(settings, router);
	const params = useExtraRequestParameters();
	const { data: pageData } = usePageDataFromId();
	const { tokenName = '', tokenValue = '', tokenExternalValue = '' } = pageData ?? {};
	const { data, error } = useSWR(
		tokenName && storeId
			? [
					shrink({
						tokenName,
						tokenValue,
						tokenExternalValue,
						storeId,
						catalogId,
						langId,
						...getContractIdParamFromContext(user?.context),
					}),
					DATA_KEY,
			  ]
			: null,
		async ([props]) => dataMap(await fetcher(true)(expand(props), params),settings)
	);
	return { uspsDeliveryTerms: data, uniqueId: tokenValue, error };
};